import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react'

interface VideoPlayerProps {
  src: string
  className?: string
  onVideoLoaded?: (width: number, height: number) => void
  onClick?: (e: Event) => void
  onPointerDown?: (e: any) => void
  onPointerUp?: (e: any) => void
}

export interface VideoPlayerRef {
  player: HTMLVideoElement | null
}

const getMimeType = (url: string): string => {
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'mp4':
      return 'video/mp4'
    case 'webm':
      return 'video/webm'
    case 'ogg':
      return 'video/ogg'
    case 'm3u8':
      return 'application/x-mpegURL'
    case 'mpd':
      return 'application/dash+xml'
    default:
      return 'video/mp4'
  }
}

export const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
  (
    { src, className, onVideoLoaded, onClick, onPointerDown, onPointerUp },
    ref,
  ) => {
    const videoRef = useRef<HTMLVideoElement | null>(null)

    useImperativeHandle(ref, () => ({
      player: videoRef.current,
    }))

    useEffect(() => {
      const videoEl = videoRef.current
      const wrapper = videoEl?.parentElement
      if (!videoEl || !wrapper) return

      const handleEvent = (e: Event) => {
        onClick?.(e)
        e.stopPropagation()
        e.stopImmediatePropagation?.()
      }

      wrapper.addEventListener('pointerdown', handleEvent, true)
      wrapper.addEventListener('mousedown', handleEvent, true)
      wrapper.addEventListener('touchstart', handleEvent, true)

      return () => {
        wrapper.removeEventListener('pointerdown', handleEvent, true)
        wrapper.removeEventListener('mousedown', handleEvent, true)
        wrapper.removeEventListener('touchstart', handleEvent, true)
      }
    }, [onClick])

    // Handle src changes: keep time & play state
    useEffect(() => {
      const videoEl = videoRef.current
      if (!videoEl) return

      const currentTime = videoEl.currentTime
      const wasPlaying = !videoEl.paused && !videoEl.ended

      // Apply new source
      const type = getMimeType(src)
      // Use <source> child for proper type hinting
      // (We rebuild the sources each time to avoid stale <source> elements)
      while (videoEl.firstChild) videoEl.removeChild(videoEl.firstChild)
      const source = document.createElement('source')
      source.src = src
      source.type = type
      videoEl.appendChild(source)

      // Load new media and restore time when metadata is ready
      const onLoadedMetadata = () => {
        if (Number.isFinite(currentTime) && currentTime > 0) {
          // Clamp to duration to avoid DOMException
          const seek = Math.min(currentTime, videoEl.duration || currentTime)
          try {
            videoEl.currentTime = seek
          } catch {
            /* ignore seek errors */
          }
        }
        if (wasPlaying) {
          videoEl.play().catch(() => {
            // Autoplay policy may block this
            // (user interaction required)
          })
        }
        onVideoLoaded?.(videoEl.videoWidth, videoEl.videoHeight)
      }

      videoEl.addEventListener('loadedmetadata', onLoadedMetadata, {
        once: true,
      })
      videoEl.load()

      return () => {
        videoEl.removeEventListener('loadedmetadata', onLoadedMetadata)
      }
    }, [src, onVideoLoaded])

    // Fire onVideoLoaded on initial mount (if metadata already available)
    useEffect(() => {
      const videoEl = videoRef.current
      if (!videoEl) return
      const handler = () =>
        onVideoLoaded?.(videoEl.videoWidth, videoEl.videoHeight)
      videoEl.addEventListener('loadedmetadata', handler)
      return () => videoEl.removeEventListener('loadedmetadata', handler)
    }, [onVideoLoaded])

    return (
      <div data-vjs-player={false} className={className}>
        <video
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          ref={videoRef}
          controls
          style={{ width: '100%', height: '100%', cursor: 'pointer' }}
          playsInline
        />
      </div>
    )
  },
)

VideoPlayer.displayName = 'VideoPlayer'
